import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { useSupportContact } from '../../../../queries/profile';
import { whatsappDigits } from '../../../Devices/Component/Health/model';

function SubscriptionStatusBanner() {
  const { t } = useTranslation();
  const { companyDetails, subscriptionInfo } = useSelector((s) => s.global);

  // No ACTIVE subscription: the backend sends `subscription: null` and `last_subscription`
  // (title, status, end_date, is_free). Reading the last one in place of `subscription` runs the
  // customer through exactly the branches the old payload did (expired -> the same red banner,
  // trial -> the trial banner, ...), so nothing changes for them except the added "ended on".
  const noActive = companyDetails?.has_active_subscription === false;
  const last     = companyDetails?.last_subscription;
  const neverSubscribed = noActive && !last;
  const support  = useSupportContact(neverSubscribed).data;

  const sub     = companyDetails?.subscription ?? (noActive ? last : null);
  const pkg     = companyDetails?.package ?? (noActive && last ? { title: last.package_title, is_free: last.is_free } : null);
  const endDate = sub?.end_date ? moment(sub.end_date) : null;
  const now     = moment();

  // No active plan (never had one, or ended): the app-wide no-package state explains it.
  if (noActive) return null;

  if (neverSubscribed) {
    // Never had a plan: not "expired". The contact button exists only when a support number
    // is configured (never a broken link) - same rule as the Screen health page.
    const digits = whatsappDigits(support?.whatsapp);
    const href = digits ? `https://wa.me/${digits}` : support?.call ? `tel:${support.call}` : null;
    return (
      <div className="alert alert-warning d-flex align-items-center mb-0 rounded-0 border-0 border-bottom">
        <i className="fa-solid fa-triangle-exclamation me-2" />
        <span>{t('billing.banner_none')}</span>
        {href && (
          <a className="btn btn-sm btn-outline-dark ms-auto" href={href} target="_blank" rel="noopener noreferrer">
            {t('billing.contact_us')}
          </a>
        )}
      </div>
    );
  }

  if (!sub) return null;

  const daysLeft    = endDate ? endDate.diff(now, 'days') : null;
  const isExpired   = subscriptionInfo?.isFinished || (endDate && endDate.isBefore(now));
  const isExpiringSoon = !isExpired && daysLeft !== null && daysLeft <= 7;
  const isTrial     = pkg?.is_free;

  if (isTrial) {
    return (
      <div className="alert alert-info d-flex align-items-center mb-0 rounded-0 border-0 border-bottom">
        <i className="fa-solid fa-gift me-2" />
        <span>
          {t('billing.banner_trial_text')} <strong>{t('billing.upgrade')}</strong> {t('billing.banner_trial_unlock')}
        </span>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="alert alert-danger d-flex align-items-center mb-0 rounded-0 border-0 border-bottom">
        <i className="fa-solid fa-circle-xmark me-2" />
        <span>
          {t('billing.banner_expired')}
          {endDate && <> {t('billing.banner_expired_on', { date: endDate.format('DD MMM YYYY') })}</>}
        </span>
      </div>
    );
  }

  if (isExpiringSoon) {
    return (
      <div className="alert alert-warning d-flex align-items-center mb-0 rounded-0 border-0 border-bottom">
        <i className="fa-solid fa-triangle-exclamation me-2" />
        <span>
          {t('billing.banner_expiring_soon', { plan: pkg?.title, count: daysLeft, date: endDate.format('DD MMM YYYY') })}
        </span>
      </div>
    );
  }

  return (
    <div className="alert alert-success d-flex align-items-center mb-0 rounded-0 border-0 border-bottom">
      <i className="fa-solid fa-circle-check me-2" />
      <span>
        {t('billing.banner_active', { plan: pkg?.title, date: endDate?.format('DD MMM YYYY') ?? '--' })}
      </span>
    </div>
  );
}

export default SubscriptionStatusBanner;
